import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Icon } from "../components/tailwind";
import { Spinner } from "../utils/Spinner";
import { useSelector } from "react-redux";
import { NOOP } from "..";
import {
  setDjangoToastOpen,
  NotificationAppearance,
} from "../api/djangoToastSlice";
import { SSEClient } from "../api/SSEApi";
import {
  EXCEPTION_STATES,
  OurJobStatus,
  READY_STATES,
} from "../customers/jobs/types";
import { RootState, store } from "../utils/store";
import { formatRelativeTime } from "../utils/dateUtils";
import { bulkActionLabels } from "../ui/BulkActionsDropdown";
import { useGetCustomerQuery } from "../api/customerApi";
import { useLocalStorage } from "../utils/localStorageHook";
import { getFeatureFlag } from "../utils/featureFlags";
import { getGlobalJobsSnapshot, LightWeightJob } from "../api/jobsApi";

export const GlobalJobTracking: React.FC = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const { data: customer } = useGetCustomerQuery();
  const globalJobTrackingEnabled = getFeatureFlag(
    customer ?? null,
    "global-job-tracking"
  );

  const [streamIsActive, setStreamIsActive] = useState(false);
  const [savedJobs, setSavedJobs] = useLocalStorage<LightWeightJob[]>(
    "global_job_tracking_jobs",
    []
  );
  const [jobs, setJobs] = useState<LightWeightJob[]>(savedJobs || []);
  const [open, setOpen] = useState(false);
  const [dismissedJobIds, setDismissedJobIds] = useLocalStorage<number[]>(
    "global_job_tracking_dismissed_ids",
    []
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const previousJobsRef = useRef<Map<number, OurJobStatus>>(new Map());

  const startStream = useCallback(() => {
    if (streamIsActive)
      return {
        close: NOOP,
      };
    const sseClient = new SSEClient<LightWeightJob[]>(token);
    setStreamIsActive(true);
    sseClient.connect(
      `/api/_internal/customer/jobs/global/stream/`,
      (data) => {
        setJobs(data);
      },
      () => {
        setStreamIsActive(false);
      },
      (_, msg) => {
        setStreamIsActive(false);
        store.dispatch(
          setDjangoToastOpen({
            appearance: NotificationAppearance.ERROR,
            content: msg,
            additionalContent: "Job Status Stream ERROR",
          })
        );
      }
    );
    return {
      close: (): void => {
        sseClient.close();
        setStreamIsActive(false);
      },
    };
  }, [token, streamIsActive, setStreamIsActive]);

  // Load jobs from localStorage on mount (immediate badge before SSE connects)
  // Load jobs from localStorage on mount (immediate badge before SSE connects)
  useEffect(() => {
    if (savedJobs && savedJobs.length > 0) {
      setJobs(savedJobs);
    }
  }, []);

  // Poll for badge when popup closed
  useEffect(() => {
    if (!token || !globalJobTrackingEnabled || open) return;
    const fetchJobs = (): Promise<void> =>
      getGlobalJobsSnapshot(token).then(setJobs);
    fetchJobs();
    const id = setInterval(fetchJobs, 30000);
    return () => clearInterval(id);
  }, [token, globalJobTrackingEnabled, open]);

  // Save jobs to localStorage whenever they change
  useEffect(() => {
    if (jobs.length > 0) {
      setSavedJobs(jobs);
    }
  }, [jobs, setSavedJobs]);

  useEffect(() => {
    if (!token || !globalJobTrackingEnabled || !open) {
      return;
    }
    const { close } = startStream();
    return () => close();
  }, [token, globalJobTrackingEnabled, open]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        open &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const filteredJobs = useMemo(() => {
    const dismissedSet = new Set(dismissedJobIds || []);
    return jobs.filter((job) => !dismissedSet.has(job.id));
  }, [jobs, dismissedJobIds]);

  useEffect(() => {
    const previousJobs = previousJobsRef.current;
    const currentJobsMap = new Map<number, OurJobStatus>();

    // Check for transitions from non-ready to ready states
    jobs.forEach((job) => {
      if (dismissedJobIds.includes(job.id)) {
        return;
      }
      const previousStatus = previousJobs.get(job.id);
      const currentStatus = job.status_from_tasks_result;

      currentJobsMap.set(job.id, currentStatus);

      // If we have a previous status and it's different from current
      if (previousStatus && previousStatus !== currentStatus) {
        const wasReady = READY_STATES.includes(previousStatus);
        const isReady = READY_STATES.includes(currentStatus);

        // Transition from non-ready to ready
        if (!wasReady && isReady) {
          const actionLabel =
            bulkActionLabels?.[job.action_name] || job.action_name;
          store.dispatch(
            setDjangoToastOpen({
              appearance: NotificationAppearance.INFO,
              content: `Job "${actionLabel}" completed`,
              additionalContent: `Job #${job.id}`,
            })
          );
        }
      }
    });

    // Update the ref with current jobs
    previousJobsRef.current = currentJobsMap;
  }, [jobs]);

  const dismissJob = useCallback(
    (jobId: number): void => {
      const prevIds = dismissedJobIds || [];
      if (!prevIds.includes(jobId)) {
        setDismissedJobIds([...prevIds, jobId]);
      }
    },
    [dismissedJobIds, setDismissedJobIds]
  );

  const getStatusIcon = (status: OurJobStatus): React.ReactNode => {
    if (READY_STATES.includes(status)) {
      if (EXCEPTION_STATES.includes(status)) {
        return (
          <Icon
            name="error"
            className="tw-text-red-700 tw-text-2xl tw-leading-[1]"
          />
        );
      }
      return (
        <Icon
          name="check_circle"
          className="tw-text-green-700 tw-text-2xl tw-leading-[1]"
        />
      );
    }
    return <Spinner />;
  };

  if (!globalJobTrackingEnabled) {
    return null;
  }

  if (!globalJobTrackingEnabled) {
    return null;
  }

  const handleJobClick = (jobId: number): void => {
    if (!customer?.slug) return;
    const jobUrl = `/d/${customer.slug}/manage/jobs/${jobId}`;
    window.location.href = jobUrl;
  };

  const handleDismissClick = (e: React.MouseEvent, jobId: number): void => {
    e.stopPropagation();
    dismissJob(jobId);
  };

  return (
    <div className="tw-relative" ref={containerRef}>
      <div
        className={`tw-relative tw-cursor-pointer hover:tw-ring-2 hover:tw-ring-gray-200 tw-rounded-sm ${
          open ? "tw-ring-gray-200 tw-ring-2" : ""
        }`}
        onClick={() => setOpen((prev) => !prev)}
      >
        {filteredJobs.length > 0 && (
          <div className="tw-bg-red-300 tw-absolute -tw-right-2 -tw-top-2 tw-text-center tw-rounded-md tw-px-2">
            {filteredJobs.length}
          </div>
        )}
        <Icon name="notifications" outlined />
      </div>
      <div
        className={`tw-absolute tw-right-0 tw-bg-white tw-shadow-lg tw-border-gray-300/50 tw-border-[1px] tw-rounded-md tw-p-2 tw-w-96 tw-max-h-[800px] tw-overflow-y-auto tw-overscroll-y-contain ${
          !open ? "tw-hidden" : ""
        }`}
      >
        <h5 className="tw-text-center tw-m-1">Job Notifications</h5>
        <div className="txu-divider"></div>
        {filteredJobs.map((job) => (
          <React.Fragment key={job.id}>
            <div
              className="tw-grid tw-grid-cols-[15px_1fr] tw-gap-3 tw-px-2 tw-py-1 hover:tw-bg-slate-200 tw-rounded-sm tw-cursor-pointer tw-items-start"
              onClick={() => handleJobClick(job.id)}
            >
              {getStatusIcon(job.status_from_tasks_result)}
              <div className="tw-flex-1">
                <div className="tw-flex tw-gap-4 tw-items-baseline">
                  <div className="tw-flex-1">
                    {bulkActionLabels?.[job.action_name] || job.action_name}
                  </div>{" "}
                  <small className="tw-text-gray-500">
                    {formatRelativeTime(job.created)}
                  </small>
                </div>
                <div className="tw-flex">
                  <small className="tw-text-gray-500 tw-flex-1">
                    {job.user}
                  </small>
                  <div
                    className="tw-text-red-400 hover:tw-text-red-600 tw-cursor-pointer hover:tw-scale-110"
                    onClick={(e) => handleDismissClick(e, job.id)}
                    title="Hide"
                  >
                    <Icon name="close" className="tw-text-lg" />
                  </div>
                </div>
              </div>
            </div>
            <div className="txu-divider"></div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
